import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, AlertCircle, Package, AlertTriangle, MessageSquare, Check, Trash2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getNotifications, markNotificationRead, markAllNotificationsRead, clearNotifications } from '../services/api';
import Loader from '../components/Loader';
import { toast } from 'sonner';

const TYPE_CONFIG = {
  reminder: { icon: AlertCircle, color: 'bg-red-100 text-red-500' },
  upcoming: { icon: Clock, color: 'bg-orange-100 text-orange-500' },
  stock_alert: { icon: Package, color: 'bg-red-100 text-red-500' },
  expiry_alert: { icon: AlertTriangle, color: 'bg-amber-100 text-amber-500' },
  custom: { icon: MessageSquare, color: 'bg-blue-100 text-blue-500' },
};

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    try {
      const res = await getNotifications();
      setNotifications(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleMarkRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (e) { toast.error('Hata oluştu'); }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('Tümü okundu');
    } catch (e) { toast.error('Hata oluştu'); }
  };

  const handleClearAll = async () => {
    try {
      await clearNotifications();
      setNotifications([]);
      toast.success('Bildirimler temizlendi');
    } catch (e) { toast.error('Hata oluştu'); }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dk önce`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} saat önce`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} gün önce`;
    return d.toLocaleDateString('tr-TR');
  };

  if (loading) return <Loader fullScreen />;

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      <header className="bg-white px-4 pt-12 pb-4 shadow-sm sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-gray-100" data-testid="back-btn">
              <ArrowLeft size={24} className="text-gray-900" />
            </button>
            <h1 className="text-xl font-bold text-gray-900 font-['Plus_Jakarta_Sans']">Bildirimler</h1>
            {unreadCount > 0 && (
              <span className="text-xs font-bold text-white bg-[#FF3B30] rounded-full px-2 py-0.5" data-testid="unread-badge">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-[#6b6de3] text-xs" data-testid="mark-all-read-btn">
                <Check size={14} className="mr-1" /> Tümünü Oku
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="p-4 space-y-2">
        {notifications.length > 0 ? (
          <>
            {notifications.map((n) => {
              const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.custom;
              const Icon = cfg.icon;
              return (
                <div
                  key={n.id}
                  onClick={() => !n.read && handleMarkRead(n.id)}
                  className={`ios-card flex items-start gap-3 cursor-pointer transition-all ${
                    !n.read ? 'border-l-4 border-[#6b6de3]' : 'opacity-70'
                  }`}
                  data-testid={`notification-${n.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${cfg.color}`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className={`text-sm font-semibold text-gray-900 truncate ${!n.read ? '' : 'text-gray-500'}`}>
                        {n.title}
                      </p>
                      {!n.read && <div className="w-2 h-2 rounded-full bg-[#6b6de3] shrink-0" />}
                    </div>
                    <p className="text-xs text-[#8E8E93] mt-0.5 line-clamp-2">{n.message}</p>
                    <p className="text-[10px] text-[#C7C7CC] mt-1">{formatTime(n.created_at)}</p>
                  </div>
                </div>
              );
            })}

            <div className="pt-4">
              <Button
                variant="outline"
                onClick={handleClearAll}
                className="w-full rounded-full text-[#FF3B30] border-[#FF3B30]/30 hover:bg-red-50"
                data-testid="clear-all-btn"
              >
                <Trash2 size={16} className="mr-2" /> Bildirimleri Temizle
              </Button>
            </div>
          </>
        ) : (
          <div className="ios-card text-center py-12">
            <Bell size={48} className="text-[#C7C7CC] mx-auto mb-3" />
            <p className="text-[#8E8E93] font-medium">Bildirim yok</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Notifications;
